import { useHasCart } from '@/feature/cart/hooks/useHasCart'
import { addToCartDrawerDataAtom } from '@/feature/cart/services/cart.store'
import { useAddToCartTracking } from '@/feature/cart/services/cart.tracking'
import { trackAddToCartInLocalStorage } from '@/feature/cart/services/cart.tracking.addToCart'
import { useAddProductToCart } from '@/feature/product/hooks/useAddProductToCart'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { QuantityPicker } from '@/ui/form/inputs/quantity-picker/QuantityPicker.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { createNativeToastIntent } from '@/ui/toast/toast.intents'
import { ClickAndCollectStatus, ProductType } from '@gql-generated/graphql'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { createProductAddedToCartIntent } from '../../services/product.intents'
import {
  productBuyBoxAddToCartAtom,
  selectedReferenceAtom,
  showMonoDiscriminantInfoCardAtom,
} from '../../services/product.store'
import type { MergedProduct } from '../../types/mergedProduct'
import { canDisplayEquivalentProductsLink } from '../../utils/productBuyBox'
import { EquivalentProductsLink } from '../equivalent-products/EquivalentProductsLink.client'
import type { ProductAvailabilityIssue } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductAvailabilityIssueCard } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'

type ProductBuyBoxAddToCartProps = {
  product: MergedProduct
  cartId?: string
  isMonoDiscriminant: boolean
  filterGroupRef: RefObject<HTMLDivElement | null>
  availabilityIssue?: ProductAvailabilityIssue
  hasPrice?: boolean
  favoritePOSPhoneNumber?: string
  isLoading?: boolean
}

export function ProductBuyBoxAddToCart({
  product,
  cartId,
  isMonoDiscriminant,
  filterGroupRef,
  availabilityIssue,
  hasPrice,
  favoritePOSPhoneNumber,
  isLoading,
}: ProductBuyBoxAddToCartProps) {
  const { t } = useTranslation(['productBuyBox', 'checkoutCart', 'generic'])
  const hasCart = useHasCart(cartId)
  const pushAddToCartEvent = useAddToCartTracking()
  const [productBuyBoxAddToCart, setProductBuyBoxAddToCart] = useAtom(productBuyBoxAddToCartAtom)
  const setShowMonoDiscriminantInfoCard = useSetAtom(showMonoDiscriminantInfoCardAtom)
  const filterSelectedArticle = useAtomValue(selectedReferenceAtom)
  const setAddToCartDrawerData = useSetAtom(addToCartDrawerDataAtom)
  const { isEquivalentProductsLinkVisible, reference, filters, type } = product
  const maxQuantity = product.stock?.maxQuantity
  const isArticle = type === ProductType.Article

  const barRef = useRef<HTMLDivElement>(null)
  const fixedBarRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Assumes the nearest positioned ancestor is the fixed bar in ProductBuyBox.client.tsx.
    fixedBarRef.current = (barRef.current?.offsetParent as HTMLDivElement | null) ?? barRef.current
  }, [])

  const scrollToBuyBoxFilterGroup = useCallback(() => {
    requestAnimationFrame(() => {
      filterGroupRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }, [filterGroupRef])
  const [addProductToCart, { loading: isAddingToCart }] = useAddProductToCart({
    variables: {
      product,
      reference,
      quantity: productBuyBoxAddToCart.quantity,
      cartId,
    },
    onCompleted: (outcome) => {
      const source =
        filters?.length === 1 && isArticle ? 'fiche produit mono-discriminant' : 'fiche article'
      trackAddToCartInLocalStorage([reference], source)
      pushAddToCartEvent([
        {
          reference,
          quantity: productBuyBoxAddToCart.quantity,
          source,
          price: 0,
        },
      ])

      const addedCartItem = outcome.addedItems[0]

      if (!addedCartItem) {
        handleIntent(
          createNativeToastIntent('error', `add-to-cart-${reference}`, {
            title: t('checkoutCart:productReference.error.title'),
            message: t('checkoutCart:addToCart.errors.default'),
          }),
        )

        return
      }

      handleIntent(createProductAddedToCartIntent())

      handleIntent(
        createNativeToastIntent('success', `add-to-cart-${reference}`, {
          title: t('productBuyBox:addToCart.success.title'),
          message: t('productBuyBox:addToCart.success.message', { reference }),
        }),
        {
          whenInBrowser: () => {
            setAddToCartDrawerData({
              isOpen: true,
              cartItems: [addedCartItem],
              quantity: productBuyBoxAddToCart.quantity,
              cartId: outcome.cartId,
              clickAndCollectStatus: outcome.hasClickAndCollectOnlyItems
                ? ClickAndCollectStatus.ClickAndCollectOnly
                : ClickAndCollectStatus.Available,
            })
          },
        },
      )
    },
  })

  const handleAddToCartSubmit = () => {
    if (!hasCart) {
      return
    }

    if (isMonoDiscriminant && !filterSelectedArticle) {
      pushToGtmDataLayer({
        event: 'gaEvent',
        eventCategory: 'fiche produit mono-discriminant',
        eventAction: 'erreur ajout panier',
        eventLabel: 'aucun discriminant sélectionné',
      })
      setShowMonoDiscriminantInfoCard(true)
      scrollToBuyBoxFilterGroup()

      return
    }

    addProductToCart()
  }

  const showEquivalentProductsLink =
    isEquivalentProductsLinkVisible &&
    canDisplayEquivalentProductsLink({
      canAddToCart: product.canAddToCart,
      availabilityIssue,
    })

  // Hide add-to-cart when the codag is not buyable, or when the product is multi-discriminant
  const isAddToCartHidden = !product.canAddToCart && !isMonoDiscriminant

  if (isAddToCartHidden && !showEquivalentProductsLink && !availabilityIssue && !isLoading) {
    return null
  }

  return (
    <div ref={barRef} className="flex flex-col gap-2">
      {availabilityIssue && hasPrice && (
        <ProductAvailabilityIssueCard
          issue={availabilityIssue}
          favoritePOSPhoneNumber={favoritePOSPhoneNumber}
        />
      )}

      {showEquivalentProductsLink && <EquivalentProductsLink slug={product.urlKey ?? ''} />}

      {!isAddToCartHidden && (
        <div className="flex w-full max-w-full gap-2 desktop:w-auto">
          <QuantityPicker
            value={productBuyBoxAddToCart.quantity ?? 1}
            onQuantityChange={(quantity) => {
              setProductBuyBoxAddToCart({
                ...productBuyBoxAddToCart,
                quantity,
              })
            }}
            size="md"
            maxQuantity={maxQuantity}
            intent={INTENT_SAME_AS_WEB}
            step={
              product.stock?.packaging?.incrementQuantity ?? product.stock?.packaging?.quantity ?? 1
            }
            minQuantity={product.stock?.packaging?.minimalSalesQuantity}
          />
          <Button
            size="md"
            className="!h-auto flex-1 grow desktop:overflow-hidden desktop:text-ellipsis desktop:[&>.btn-text]:hidden desktop-md:[&>.btn-text]:inline"
            variant="primary"
            iconLeft={<Icon.ShoppingBasket />}
            onPress={handleAddToCartSubmit}
            isLoading={isAddingToCart || isLoading}
            intent={INTENT_SAME_AS_WEB}
          >
            {t('generic:addToCart.ctaLabel')}
          </Button>
        </div>
      )}
    </div>
  )
}
